/**
 * MemoPage - メモの編集画面（一覧は左ドロワーで表示）
 *
 * - 対象メモは ?id=<memoId> クエリで指定する
 * - id 未指定/不在のときは最新メモへ自動遷移。メモが1件もなければ空メモを自動作成する
 * - メモの切り替え・新規作成・削除は Header 下のハンバーガーから開く左ドロワーで行う
 */

import type { Editor } from '@tiptap/react'
import { History, StickyNote } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { type BookNoteListItem, listBookNotes } from '@/api/services/book-note'
import type { TextDocumentRevision } from '@/api/services/memo'
import { BacklinksPanel } from '@/components/memo/backlinks-panel'
import { HamburgerIcon } from '@/components/memo/hamburger-icon'
import { LocalGraphPanel } from '@/components/memo/local-graph-panel'
import { MemoHistorySheet } from '@/components/memo/memo-history-sheet'
import { MemoListDrawer } from '@/components/memo/memo-list-drawer'
import { MemoRevisionPreview } from '@/components/memo/memo-revision-preview'
import { RelatedPanel } from '@/components/memo/related-panel'
import { ShortcutHelp } from '@/components/memo/shortcut-help'
import { UnlinkedMentionsPanel } from '@/components/memo/unlinked-mentions-panel'
import { Header } from '@/components/shared/header'
import { NotionEditor } from '@/components/shared/notion-editor'
import { useGoBack } from '@/hooks/useGoBack'
import { parseUtcDate } from '@/lib/date-utils'
import { clearNotionEditorBackup } from '@/lib/notion-editor-backup'
import { cn } from '@/lib/utils'
import { bookNotePath, PATHS } from '@/routes/paths'
import { useMemoStore } from '@/store/useMemoStore'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const STATUS_LABEL: Record<SaveStatus, string> = {
  idle: '',
  saving: '保存中…',
  saved: '保存済み',
  error: '保存失敗',
}

export const MemoPage = () => {
  const navigate = useNavigate()
  // 読書ノートの [[ リンクなどから開いたときは、その画面へ戻る
  const goBack = useGoBack(PATHS.HOME)
  const [searchParams] = useSearchParams()
  const idParam = searchParams.get('id')
  const memoId = idParam ? Number(idParam) : null

  const memos = useMemoStore((s) => s.memos)
  const isLoaded = useMemoStore((s) => s.isLoaded)
  const loadMemos = useMemoStore((s) => s.loadMemos)
  const addMemo = useMemoStore((s) => s.addMemo)
  const removeMemo = useMemoStore((s) => s.removeMemo)
  const renameMemoTitle = useMemoStore((s) => s.renameMemoTitle)
  const setMemoCard = useMemoStore((s) => s.setMemoCard)
  const currentMemo = memos.find((m) => m.id === memoId)

  const [title, setTitle] = useState('')
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [editor, setEditor] = useState<Editor | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [bookNotes, setBookNotes] = useState<BookNoteListItem[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  // 履歴から選んだ版。別のメモに移ったら使わないよう memoId と組で持つ
  const [preview, setPreview] = useState<{ memoId: number; revision: TextDocumentRevision } | null>(null)
  const previewRevision = preview?.memoId === memoId ? preview.revision : null
  // 履歴から戻したときにエディタを作り直して本文を読み込み直す
  const [editorReloadKey, setEditorReloadKey] = useState(0)
  // 空メモ自動作成の二重実行を防ぐ
  const creatingRef = useRef(false)

  // メモ一覧をロード
  useEffect(() => {
    if (!isLoaded) void loadMemos()
  }, [isLoaded, loadMemos])

  useEffect(() => {
    if (!drawerOpen) return
    let cancelled = false
    listBookNotes()
      .then((items) => {
        if (!cancelled) setBookNotes(items)
      })
      .catch((err) => console.warn('[memo] 読書ノートの一覧を取得できませんでした', err))
    return () => {
      cancelled = true
    }
  }, [drawerOpen])

  // 対象メモの解決: id 未指定/不在なら最新へ。0件なら空メモを自動作成
  useEffect(() => {
    if (!isLoaded) return
    const target = memoId !== null && !Number.isNaN(memoId) ? memos.find((m) => m.id === memoId) : undefined
    if (target) return

    if (memos.length > 0) {
      const latest = [...memos].sort(
        (a, b) => parseUtcDate(b.updated_at).getTime() - parseUtcDate(a.updated_at).getTime(),
      )[0]
      navigate(`${PATHS.MEMO.LIST}?id=${latest.id}`, { replace: true })
      return
    }

    if (creatingRef.current) return
    creatingRef.current = true
    void (async () => {
      const memo = await addMemo('')
      if (!memo) {
        toast.error('メモの作成に失敗しました')
        creatingRef.current = false
        return
      }
      navigate(`${PATHS.MEMO.LIST}?id=${memo.id}`, { replace: true })
    })()
  }, [isLoaded, memoId, memos, navigate, addMemo])

  // タイトルを同期
  useEffect(() => {
    const memo = memos.find((m) => m.id === memoId)
    if (memo) setTitle(memo.title)
  }, [memos, memoId])

  const handleTitleBlur = () => {
    if (memoId === null) return
    const memo = memos.find((m) => m.id === memoId)
    if (memo && memo.title !== title) {
      void renameMemoTitle(memoId, title)
    }
  }

  const handleSelect = (id: number) => {
    setDrawerOpen(false)
    navigate(`${PATHS.MEMO.LIST}?id=${id}`)
  }

  const handleCreate = async (isCard: boolean) => {
    const memo = await addMemo('', isCard)
    if (!memo) {
      toast.error('メモの作成に失敗しました')
      return
    }
    setDrawerOpen(false)
    navigate(`${PATHS.MEMO.LIST}?id=${memo.id}`)
  }

  const handleDelete = async (id: number) => {
    await removeMemo(id)
    toast.success('削除しました')
    // 削除したメモを開いていた場合は解決 useEffect が最新/新規へ誘導する
    if (id === memoId) {
      navigate(PATHS.MEMO.LIST, { replace: true })
    }
  }

  const handleRestored = () => {
    if (memoId === null) return
    clearNotionEditorBackup('memo', memoId)
    setPreview(null)
    setHistoryOpen(false)
    setEditorReloadKey((k) => k + 1)
  }

  const isValidMemoId = memoId !== null && !Number.isNaN(memoId)

  return (
    <div className="flex h-dvh flex-col">
      <Header
        title={
          <div className="flex w-full items-center gap-2">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              placeholder="無題のメモ"
              className="min-w-0 flex-1 bg-transparent text-lg font-semibold outline-none placeholder:text-muted-foreground"
            />
            <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">{STATUS_LABEL[status]}</span>
            <button
              type="button"
              onClick={() => currentMemo && void setMemoCard(currentMemo.id, !currentMemo.is_card)}
              disabled={!currentMemo}
              aria-pressed={currentMemo?.is_card ?? false}
              aria-label="カードにする"
              title={currentMemo?.is_card ? 'カード（タップで通常のメモに戻す）' : 'カードにする'}
              className={cn(
                'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md hover:bg-accent',
                currentMemo?.is_card ? 'text-amber-500' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <StickyNote className="size-4" fill={currentMemo?.is_card ? 'currentColor' : 'none'} />
            </button>
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              disabled={memoId === null}
              aria-label="履歴"
              title="履歴"
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <History className="size-4" />
            </button>
            <ShortcutHelp />
          </div>
        }
        onClose={goBack}
      />

      {/* ヘッダー下・左上に固定するハンバーガー。ドロワー開閉に追従して右へスライドし × に変化する */}
      {/* 開いたときはドロワー(w-72=288px)の右端の外側へ出して内容と重ならないようにする */}
      {/* 履歴の版を表示中は、上部の帯と重なるので出さない */}
      {!previewRevision && (
        <button
          type="button"
          onClick={() => setDrawerOpen((v) => !v)}
          aria-label="メモ一覧"
          aria-expanded={drawerOpen}
          className={`fixed top-16 left-3 z-60 flex size-9 cursor-pointer items-center justify-center rounded-md text-foreground transition-transform duration-300 ease-in-out ${
            drawerOpen ? 'translate-x-70' : 'translate-x-0'
          }`}
        >
          <HamburgerIcon open={drawerOpen} />
        </button>
      )}

      <div className="flex-1 overflow-x-hidden overflow-y-auto">
        {/* モバイルは NotionEditor 内部の padding (1.5rem) だけに任せて画面幅を最大化する。
             sm 以上では余白を持たせて可読性を上げる。 */}
        {/* 履歴シートが下半分を覆うので、その分本文を上までスクロールできるようにする */}
        <div className={`w-full max-w-full py-4 sm:px-8 ${historyOpen ? 'pb-[50dvh]' : 'pb-24'}`}>
          {isValidMemoId && previewRevision && (
            <MemoRevisionPreview
              entityId={memoId}
              revision={previewRevision}
              onClose={() => setPreview(null)}
              onRestored={handleRestored}
            />
          )}
          {isValidMemoId && !previewRevision && (
            <>
              <NotionEditor
                key={`${memoId}:${editorReloadKey}`}
                entityType="memo"
                entityId={memoId}
                placeholder="入力してください…「[[」でメモや本にリンク"
                onStatusChange={setStatus}
                onEditorReady={setEditor}
              />
              <BacklinksPanel targetType="memo" targetId={memoId} />
              <UnlinkedMentionsPanel memoId={memoId} title={currentMemo?.title ?? ''} />
              <RelatedPanel kind="memo" refId={memoId} editor={editor} />
              <LocalGraphPanel centerId={`memo:${memoId}`} />
            </>
          )}
        </div>
      </div>

      <MemoListDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        memos={memos}
        activeId={memoId}
        onSelect={handleSelect}
        onCreate={() => void handleCreate(false)}
        onCreateCard={() => void handleCreate(true)}
        onOpenGraph={() => {
          setDrawerOpen(false)
          navigate(PATHS.MEMO.GRAPH)
        }}
        onDelete={handleDelete}
        bookNotes={bookNotes}
        onSelectBookNote={(productId) => {
          setDrawerOpen(false)
          navigate(bookNotePath(productId))
        }}
      />

      {isValidMemoId && (
        <MemoHistorySheet
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          entityId={memoId}
          selectedId={previewRevision?.id ?? null}
          onSelect={(revision) => setPreview({ memoId, revision })}
        />
      )}
    </div>
  )
}
